import { InventoryColumnState } from './inventoryApiTypes';
import {
  Named,
  PermissionRole,
  PermissionOrgGroup,
  Organization,
  PrivilegesByOrganization,
  AssetType,
} from './apiTypes';
import { ArdoqId, Email, Entity, MetaData, Versioned } from './coreTypes';

/**
 * There are plenty more for the different views
 */
export enum PersonalSetting {
  DEFAULT = 'default',
  LOCALE = 'locale',
  LANGUAGE = 'language',
  ORGANIZATION = 'organization',
  ENFORCE_FILTERS_DIALOG = 'enforceFiltersDialogUserSettingKey',
  FAVORITE_TEMPLATES = 'favoriteTemplates',
  FAVORITE = 'favorite',
  /**
   * A triplet of last used reference type name by source and target component type names.
   * "source component type name" -> "target component type name" -> "reference type name"
   */
  LAST_USED_REFERENCE_TYPE_NAME_TRIPLES = 'lastUsedReferenceTypeNameTriples',
  SHOWN_EMAIL_PREFERENCES = 'shownEmailPreferences',
  DISABLE_REFERENCE_TIPS = 'disableReferenceTip',
  HIDE_MODEL_PREVIEW = 'hideModelPreview',
  LAST_VIEWED_DASHBOARD = 'lastViewedDashboard',
  HIDE_ON_CLOSE_SCENARIO_WARNING = 'hideOnCloseScenarioWarningDialog',
  QUICK_PERSPECTIVES_CONTAINER_EXPANDED = 'quickPerspectivesContainerExpanded',
  HAS_SEEN_SSO_ATTRIBUTE_MAPPING = 'hasSeenSsoAttributeMapping',
  HIDE_LWUI_NAVIGATION_TIPS_MODAL = 'hideLWUINavigationTipsModal',
  USER_VISITED_DISCOVER = 'userVisitedDiscover',
  DISCOVER_SIDEBAR_WIDTH = 'discoverSidebarWidth',
  HAS_SEEN_INVENTORY = 'hasSeenInventory',
  INCLUDE_CLICK_OTHER_COMPONENT_TYPE_NODES_HINTS_IN_VIEWPOINT_BUILDER = 'includeClickOtherComponentTypeNodesHintsInViewpointBuilder',
  DONT_SHOW_REMOVE_DATASET_CONFIRMATION = 'dontShowRemoveDatasetConfirmation',
  DONT_SHOW_CLOSE_VIEW_BUILDER_CONFIRMATION = 'dontShowCloseViewBuilderConfirmation',
  AUTOLINK_AND_DATE_DASHBOARD_FILTER_NOTIFICATION = 'autoLinkAndDateDashboardFilterNotification',
  USE_CASE_UPDATES_SEEN = 'useCaseUpdatesSeen',
  DONT_SHOW_OPEN_DATASET_CONFIRMATION = 'dontShowOpenNewDatasetConfirmation',
  DONT_SHOW_OPEN_WORKSPACE_CONFIRMATION = 'dontShowOpenWorkspaceConfirmation',
  DONT_SHOW_FULL_HIERARCHY_CONFIRMATION = 'dontShowFullHierarchyConfirmation',
  ASSETS_BROWSER_PER_PAGE = 'assetsBrowserPerPage',
  GENERIC_SHOW_FILTERED_COMPONENTS = 'generic_showFilteredComponents',
  HOMEPAGE_VIEWPOINTS_STATE = 'homepageViewpointsState',
  INVENTORY_RECENT_COLUMN_SELECTION_PER_WORKSPACE_OR_TYPE = 'inventoryRecentColumnSelectionPerWorkspaceOrType',
  USER_ASSETS_OVERVIEW_TAB_VISITED = 'userAssetsOverviewVisited',
  PRESENTATION_OVERVIEW_STATE = 'presentationOverviewState',
  HAS_SEEN_ACCESS_OVERVIEW_PAGE = 'hasSeenAccessOverviewPage',
  NAVIGATION_SIDEBAR_STATE = 'navigationSidebarState',
  CONFIDENTIAL_FIELDS_ONBOARDING_DISMISSED = 'confidentialFieldsOnboardingDismissed',
  HAS_SEEN_ASSISTANT_DISPLAY_MODE_HINT = 'hasSeenAssistantDisplayModeHint',
  ASSISTANT_PREFERENCES = 'assistantPreferences',
  ON_BOARDED_METAMODEL_PREVIEW = 'onBoardedMetamodelPreview',

  /**
   * Ephemeral-notification personal settings come in two shapes:
   * - `EPHEMERAL_NOTIFICATION_DISMISSED_KEYS` — shared ledger of dismissed ids
   *   ("shown once per user" and `dependsOnDismissed` sequencing).
   * - `EPHEMERAL_NOTIFICATION_*_FIRST_SHOWN` — per-notification ISO timestamp
   *   for a rolling window of `FIRST_SEEN_ROLLING_WINDOW_DAYS`.
   *   See `EphemeralFirstSeenSettingKey`.
   *
   * Wire values are legacy — don't rename without a backend migration. New
   * first-shown keys follow the `EPHEMERAL_NOTIFICATION_<key>_FIRST_SHOWN`
   * symbol convention.
   */
  EPHEMERAL_NOTIFICATION_DISMISSED_KEYS = 'ephemeralDismissedBanners',
  EPHEMERAL_NOTIFICATION_Q1_2026_RELEASE_FIRST_SHOWN = 'q12026ReleaseFirstShown',
}

export type APICurrentUserFavorites = {
  dashboards: ArdoqId[];
  [AssetType.FOLDER]: ArdoqId[];
  [AssetType.METAMODEL]: ArdoqId[];
  [AssetType.PRESENTATION]: ArdoqId[];
  [AssetType.SCENARIO]: ArdoqId[];
  [AssetType.SURVEY]: ArdoqId[];
  [AssetType.WORKSPACE]: ArdoqId[];
  [AssetType.REPORT]: ArdoqId[];
  [AssetType.DASHBOARD]: ArdoqId[];
  [AssetType.VIEWPOINT]: ArdoqId[];
  [AssetType.BROADCAST]: ArdoqId[];
  [AssetType.TRAVERSAL]: ArdoqId[];
  [AssetType.BOOKMARK]: ArdoqId[];
};

export type APICurrentUserOrganizations = Omit<
  Organization,
  'is-trial' | 'days-remaining-trial' | 'expired'
>;

type HomepageViewpointsStatePersonalSettings = {
  activeTab: 'lastUpdated' | 'favorites';
  isBannerDismissed: boolean;
  isExpanded: boolean;
};

export type InventoryRecentColumnSelectionPerWorkspaceOrTypePersonalSettings =
  Record<
    string, // workspace id or type id in the future
    { columns: InventoryColumnState[] }
  >;

type PresentationOverviewStatePersonalSettings = {
  isInListMode: boolean;
};

export type AssistantPreferences = {
  mode?: 'closed' | 'open';
  displayMode?: 'floating' | 'sidePanel';
  panelWidth?: number;
};

export type PersonalSettingMap = {
  [PersonalSetting.QUICK_PERSPECTIVES_CONTAINER_EXPANDED]: boolean;
  [PersonalSetting.FAVORITE]: Partial<APICurrentUserFavorites>;
  [PersonalSetting.FAVORITE_TEMPLATES]: ArdoqId[];
  [PersonalSetting.HIDE_MODEL_PREVIEW]: boolean;
  [PersonalSetting.LOCALE]?: string;
  [PersonalSetting.LANGUAGE]?: string;
  [PersonalSetting.HIDE_LWUI_NAVIGATION_TIPS_MODAL]?: boolean;
  [PersonalSetting.USER_VISITED_DISCOVER]?: boolean;
  [PersonalSetting.DISCOVER_SIDEBAR_WIDTH]?: string;
  [PersonalSetting.INCLUDE_CLICK_OTHER_COMPONENT_TYPE_NODES_HINTS_IN_VIEWPOINT_BUILDER]?: boolean;
  [PersonalSetting.DONT_SHOW_REMOVE_DATASET_CONFIRMATION]?: boolean;
  [PersonalSetting.DONT_SHOW_OPEN_DATASET_CONFIRMATION]?: boolean;
  [PersonalSetting.DONT_SHOW_OPEN_WORKSPACE_CONFIRMATION]?: boolean;
  [PersonalSetting.DONT_SHOW_FULL_HIERARCHY_CONFIRMATION]?: boolean;
  [PersonalSetting.DONT_SHOW_CLOSE_VIEW_BUILDER_CONFIRMATION]?: boolean;
  [PersonalSetting.ASSETS_BROWSER_PER_PAGE]?: { [key: string]: number };
  [PersonalSetting.GENERIC_SHOW_FILTERED_COMPONENTS]?: boolean;
  [PersonalSetting.HOMEPAGE_VIEWPOINTS_STATE]?: HomepageViewpointsStatePersonalSettings;
  [PersonalSetting.INVENTORY_RECENT_COLUMN_SELECTION_PER_WORKSPACE_OR_TYPE]?: InventoryRecentColumnSelectionPerWorkspaceOrTypePersonalSettings; // TODO AM remove when backend clears this up
  [PersonalSetting.PRESENTATION_OVERVIEW_STATE]?: PresentationOverviewStatePersonalSettings;
  [PersonalSetting.AUTOLINK_AND_DATE_DASHBOARD_FILTER_NOTIFICATION]?: boolean;
  [PersonalSetting.HAS_SEEN_ACCESS_OVERVIEW_PAGE]?: boolean;
  [PersonalSetting.NAVIGATION_SIDEBAR_STATE]?: 'open' | 'closed';
  [PersonalSetting.SHOWN_EMAIL_PREFERENCES]?: boolean;
  [PersonalSetting.ORGANIZATION]?: string;
  [PersonalSetting.DEFAULT]?: { digest: string };
  [PersonalSetting.CONFIDENTIAL_FIELDS_ONBOARDING_DISMISSED]?: boolean;
  [PersonalSetting.USE_CASE_UPDATES_SEEN]?: ArdoqId[];
  [PersonalSetting.USER_ASSETS_OVERVIEW_TAB_VISITED]?: boolean;
  [PersonalSetting.HAS_SEEN_ASSISTANT_DISPLAY_MODE_HINT]?: boolean;
  [PersonalSetting.ASSISTANT_PREFERENCES]?: AssistantPreferences;
  [PersonalSetting.ON_BOARDED_METAMODEL_PREVIEW]?: boolean;
  [PersonalSetting.EPHEMERAL_NOTIFICATION_DISMISSED_KEYS]?: string[];
  [PersonalSetting.EPHEMERAL_NOTIFICATION_Q1_2026_RELEASE_FIRST_SHOWN]?: string;
};

// The key is the persisted id of the feature flag
export type APIUserFeatureToggles = Record<string, boolean | undefined>;

export type APICurrentUser = Entity &
  MetaData &
  Versioned &
  Named & {
    email: Email;
    /*
     * True of user has admin or writer role.
     * See `org-util/writer+?` in api
     */
    writeAccess: boolean;
    settings: Partial<PersonalSettingMap>;
    features: APIUserFeatureToggles;
    roles: PermissionRole[];
    groups?: PermissionOrgGroup[];
    organization: Organization;
    organizations: APICurrentUserOrganizations[];
    'analytics-hash': string;
    verified: boolean;
    'accepted-terms': boolean;
    privileges: PrivilegesByOrganization;
    /**
     * Present only while an admin is impersonating this user. Carries the real
     * (impersonating) admin's id and name; its presence is the marker of an
     * active impersonation session.
     */
    impersonatedBy?: { _id: ArdoqId; name: string };
  };
